import { useState, type ReactNode } from 'react';
import { useVault } from '../lib/vault';
import { isHintHidden, type HintId } from '@shared/types';
import { plural } from '@shared/text';
import { useAction } from './ui';

/**
 * Ein Hinweis auf der Übersicht.
 *
 * Anklickbar (führt zu den betroffenen Einträgen) und wegklickbar - entweder
 * für ein paar Tage oder dauerhaft. Zurückgestellt wird pro Person: was die
 * eine erledigt hat, muss die andere nicht auch noch wegräumen.
 */
export function Hint({
  id,
  tone = 'warn',
  title,
  children,
  count,
  onShow,
  showLabel = 'Anzeigen',
}: {
  id: HintId;
  tone?: 'warn' | 'danger' | 'info' | 'ok';
  title: ReactNode;
  children?: ReactNode;
  /** Wie viele Einträge betroffen sind – steht auf dem Anzeigen-Knopf. */
  count?: number;
  /** Führt zu den betroffenen Einträgen. Fehlt sie, gibt es keinen Knopf. */
  onShow?: () => void;
  showLabel?: string;
}): JSX.Element | null {
  const { dismissedHints, setHint } = useVault();
  const { busy, run } = useAction();
  const [snoozeOpen, setSnoozeOpen] = useState(false);

  if (isHintHidden(dismissedHints, id)) return null;

  const klasse =
    tone === 'danger' ? 'note note-danger' : tone === 'ok' ? 'note note-ok' : tone === 'info' ? 'note' : 'note note-warn';

  const zurueckstellen = (tage: number): void => {
    const bis = new Date(Date.now() + tage * 86_400_000).toISOString();
    void run(() => setHint(id, bis));
    setSnoozeOpen(false);
  };

  return (
    <div className={`${klasse} hint`} style={{ marginTop: 14 }}>
      <strong>{title}</strong>
      {children ? <div className="small" style={{ marginTop: 6 }}>{children}</div> : null}

      <div className="hint-actions">
        {onShow ? (
          <button className="btn-sm" onClick={onShow}>
            {showLabel}
            {count !== undefined ? ` (${count})` : ''}
          </button>
        ) : null}

        {snoozeOpen ? (
          <>
            <span className="small">Ruhe für</span>
            {[7, 30, 90].map((tage) => (
              <button key={tage} className="btn-sm" disabled={busy} onClick={() => zurueckstellen(tage)}>
                {tage} Tage
              </button>
            ))}
            <button
              className="btn-quiet btn-sm"
              disabled={busy}
              onClick={() => void run(() => setHint(id, null))}
            >
              dauerhaft ignorieren
            </button>
            <button className="btn-quiet btn-sm" onClick={() => setSnoozeOpen(false)}>
              Abbrechen
            </button>
          </>
        ) : (
          <button className="btn-quiet btn-sm" disabled={busy} onClick={() => setSnoozeOpen(true)}>
            Zurückstellen
          </button>
        )}
      </div>
    </div>
  );
}

/** Zeigt an, wie viele Hinweise gerade unterdrückt sind, und holt sie zurück. */
export function HiddenHints(): JSX.Element | null {
  const { dismissedHints, setHint } = useVault();
  const { busy, run } = useAction();

  const versteckt = (Object.keys(dismissedHints) as HintId[]).filter((id) =>
    isHintHidden(dismissedHints, id),
  );
  if (versteckt.length === 0) return null;

  return (
    <p className="small muted" style={{ marginTop: 14 }}>
      {plural(versteckt.length, 'Hinweis ist', 'Hinweise sind')} zurückgestellt.{' '}
      <button
        className="hint-link"
        disabled={busy}
        onClick={() => void run(async () => {
          for (const id of versteckt) await setHint(id);
        })}
      >
        Wieder einblenden
      </button>
    </p>
  );
}
